import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Spinner } from "react-bootstrap";
import { IntegrationTaskType, TaskTypeEntry, compassTaskTypeGroups, compassTaskTypes } from "../../models/integrations";
import {
  fetchIntegrationMappings,
  fetchIntegrationTaskTypes,
  patchIntegrationMapping,
  postIntegrationMapping,
} from "../../utils/http/companies";

interface IntegrationMappingsProps {
  companyNameOrId: string;
  integrationName: string;
  excludeGroups?: string[];
}

type MappingView = "tasks";

interface MappingChangeItem {
  compassType: string;
  crmId: string;
  crmName: string;
}

// compassType → list of mapped CRM task type IDs (multiple allowed)
type MultiMappings = Record<string, string[]>;

function getTaskTypeName(taskType: IntegrationTaskType): string {
  const nlBE = taskType.names.find((n) => n.locale === "nl-BE" || n.locale === "nl");
  return nlBE?.name ?? taskType.names[0]?.name ?? taskType.id;
}

function getTaskTypeSourceMeta(source?: string): { icon: string; label: string } | null {
  if (source === "calendar") return { icon: "bi-calendar-event", label: "Calendar type" };
  if (source === "activity") return { icon: "bi-activity", label: "Activity type" };
  return null;
}

function TaskTypeLabel({ taskType }: { taskType: IntegrationTaskType }) {
  const meta = getTaskTypeSourceMeta(taskType.source);
  return (
    <>
      {meta && <i className={`bi ${meta.icon} me-1`} title={meta.label} aria-label={meta.label} />}
      {getTaskTypeName(taskType)}
    </>
  );
}

// Invert { crmId: { task_type, source_name } } → { compassType: crmId[] }
function invertMappings(mappings: Record<string, TaskTypeEntry>): MultiMappings {
  const result: MultiMappings = {};
  for (const [crmId, entry] of Object.entries(mappings)) {
    if (!result[entry.task_type]) result[entry.task_type] = [];
    result[entry.task_type].push(crmId);
  }
  return result;
}

// Collect all CRM IDs currently selected across all compass types, except the given one
function getUsedCrmIds(mappings: MultiMappings, excludeCompassType: string): Set<string> {
  const used = new Set<string>();
  for (const [ct, ids] of Object.entries(mappings)) {
    if (ct !== excludeCompassType) ids.forEach((id) => used.add(id));
  }
  return used;
}

function IntegrationMappings({ companyNameOrId, integrationName, excludeGroups = [] }: IntegrationMappingsProps) {
  const queryClient = useQueryClient();

  const [acknowledged, setAcknowledged] = useState(false);
  const [activeView] = useState<MappingView>("tasks");
  const [pendingMappings, setPendingMappings] = useState<MultiMappings>({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    data: taskTypesData,
    isLoading: taskTypesLoading,
    error: taskTypesError,
  } = useQuery({
    queryKey: ["integration-task-types", companyNameOrId, integrationName],
    queryFn: ({ signal }) => fetchIntegrationTaskTypes({ companyNameOrId, integrationName, signal }),
  });

  const {
    data: mappingsData,
    isLoading: mappingsLoading,
    error: mappingsError,
  } = useQuery({
    queryKey: ["integration-mappings", companyNameOrId, integrationName],
    queryFn: ({ signal }) => fetchIntegrationMappings({ companyNameOrId, integrationName, signal }),
  });

  const existingMapping = mappingsData?.find((m) => m.mapping_type === "task_type") ?? null;
  const storedMappings: Record<string, TaskTypeEntry> = existingMapping?.mappings ?? {};

  const crmTaskTypes: IntegrationTaskType[] = (taskTypesData?.task_types ?? []).sort((a, b) =>
    getTaskTypeName(a).localeCompare(getTaskTypeName(b), "nl"),
  );

  const crmTypeById = new Map(crmTaskTypes.map((t) => [t.id, t]));

  // Only keep mappings for known compass types. CRM IDs mapped to unknown compass types
  // (e.g. "technical_inventory", "contact") are not in any card group, so they'd vanish from
  // allUsedIds and correctly appear in the "Remaining CRM types" list instead.
  const validCompassTypes = new Set([...compassTaskTypes, "other"]);
  const originalMulti: MultiMappings = Object.fromEntries(
    Object.entries(invertMappings(storedMappings)).filter(([ct]) => validCompassTypes.has(ct)),
  );

  const handleAcknowledgeToggle = () => {
    if (!acknowledged) {
      // Deep-copy original into pending
      const copy: MultiMappings = {};
      for (const [ct, ids] of Object.entries(originalMulti)) {
        copy[ct] = [...ids];
      }
      setPendingMappings(copy);
    }
    setAcknowledged((prev) => !prev);
  };

  const handleAddCrmType = (compassType: string, crmId: string) => {
    if (!crmId) return;
    setPendingMappings((prev) => {
      const existing = prev[compassType] ?? [];
      if (existing.includes(crmId)) return prev;
      return { ...prev, [compassType]: [...existing, crmId] };
    });
  };

  const handleRemoveCrmType = (compassType: string, crmId: string) => {
    setPendingMappings((prev) => {
      const updated = (prev[compassType] ?? []).filter((id) => id !== crmId);
      return { ...prev, [compassType]: updated };
    });
  };

  const handleExcludeCrmType = (crmId: string) => {
    setPendingMappings((prev) => {
      const excluded = prev["other"] ?? [];
      if (excluded.includes(crmId)) return prev;
      return { ...prev, other: [...excluded, crmId] };
    });
  };

  const handleUnexcludeCrmType = (crmId: string) => {
    setPendingMappings((prev) => {
      const excluded = (prev["other"] ?? []).filter((id) => id !== crmId);
      return { ...prev, other: excluded };
    });
  };

  const computeChanges = (): {
    added: MappingChangeItem[];
    removed: MappingChangeItem[];
    excludedAdded: { crmId: string; crmName: string }[];
    excludedRemoved: { crmId: string; crmName: string }[];
  } => {
    const added: MappingChangeItem[] = [];
    const removed: MappingChangeItem[] = [];
    const excludedAdded: { crmId: string; crmName: string }[] = [];
    const excludedRemoved: { crmId: string; crmName: string }[] = [];

    const allCompassTypes = new Set([...Object.keys(originalMulti), ...Object.keys(pendingMappings)]);

    for (const compassType of allCompassTypes) {
      const originalIds = new Set(originalMulti[compassType] ?? []);
      const pendingIds = new Set(pendingMappings[compassType] ?? []);

      for (const id of pendingIds) {
        if (!originalIds.has(id)) {
          const crmType = crmTypeById.get(id);
          const crmName = crmType ? getTaskTypeName(crmType) : id;
          if (compassType === "other") {
            excludedAdded.push({ crmId: id, crmName });
          } else {
            added.push({ compassType, crmId: id, crmName });
          }
        }
      }
      for (const id of originalIds) {
        if (!pendingIds.has(id)) {
          const crmType = crmTypeById.get(id);
          const crmName = crmType ? getTaskTypeName(crmType) : id;
          if (compassType === "other") {
            excludedRemoved.push({ crmId: id, crmName });
          } else {
            removed.push({ compassType, crmId: id, crmName });
          }
        }
      }
    }

    return { added, removed, excludedAdded, excludedRemoved };
  };

  const {
    added: pendingAdded,
    removed: pendingRemoved,
    excludedAdded: pendingExcludedAdded,
    excludedRemoved: pendingExcludedRemoved,
  } = acknowledged ? computeChanges() : { added: [], removed: [], excludedAdded: [], excludedRemoved: [] };
  const hasChanges =
    pendingAdded.length > 0 ||
    pendingRemoved.length > 0 ||
    pendingExcludedAdded.length > 0 ||
    pendingExcludedRemoved.length > 0;

  const handleConfirmSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    const newMappings: Record<string, TaskTypeEntry> = {};
    for (const compassType of compassTaskTypes) {
      const ids = pendingMappings[compassType] ?? [];
      for (const crmId of ids) {
        const crmType = crmTypeById.get(crmId);
        newMappings[crmId] = {
          task_type: compassType,
          source_name: crmType ? getTaskTypeName(crmType) : crmId,
        };
      }
    }
    for (const crmId of pendingMappings["other"] ?? []) {
      const crmType = crmTypeById.get(crmId);
      newMappings[crmId] = {
        task_type: "other",
        source_name: crmType ? getTaskTypeName(crmType) : crmId,
      };
    }

    try {
      if (existingMapping?.id) {
        await patchIntegrationMapping({
          companyNameOrId,
          integrationName,
          mappingId: existingMapping.id,
          update: { mappings: newMappings },
        });
      } else {
        await postIntegrationMapping({
          companyNameOrId,
          integrationName,
          mapping: { mapping_type: "task_type", mappings: newMappings },
        });
      }

      await queryClient.invalidateQueries({
        queryKey: ["integration-mappings", companyNameOrId, integrationName],
      });

      setShowSaveModal(false);
      setAcknowledged(false);
      setPendingMappings({});
    } catch (err: any) {
      setSaveError(err?.message ?? "An error occurred while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  if (taskTypesError || mappingsError) {
    return <Alert variant="danger">Failed to load mappings data. Please try again.</Alert>;
  }

  if (taskTypesLoading || mappingsLoading || !taskTypesData || !mappingsData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <Spinner animation="border" variant="secondary" />
      </div>
    );
  }

  const currentMulti = acknowledged ? pendingMappings : originalMulti;

  const allUsedIds = new Set(Object.values(currentMulti).flat());
  const remainingCrmTypes = crmTaskTypes.filter((t) => !allUsedIds.has(t.id));
  const excludedCrmTypes = (currentMulti["other"] ?? [])
    .map((id) => crmTypeById.get(id))
    .filter((t): t is IntegrationTaskType => t !== undefined)
    .sort((a, b) => getTaskTypeName(a).localeCompare(getTaskTypeName(b), "nl"));

  const remainingCalendar = remainingCrmTypes.filter((t) => t.source === "calendar");
  const remainingActivity = remainingCrmTypes.filter((t) => t.source === "activity");
  const remainingOther = remainingCrmTypes.filter((t) => t.source !== "calendar" && t.source !== "activity");

  const renderRemainingGroup = (label: string, types: IntegrationTaskType[]) =>
    types.length > 0 && (
      <div className="mb-2">
        <div className="text-muted mb-1" style={{ fontSize: "0.7rem" }}>
          {label}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {types.map((t) => (
            <Badge
              key={t.id}
              bg="secondary"
              style={{ fontSize: "0.75rem", fontWeight: 400, cursor: acknowledged ? "pointer" : "default" }}
              onClick={acknowledged ? () => handleExcludeCrmType(t.id) : undefined}
              title={acknowledged ? "Click to mark as not to be mapped" : undefined}
            >
              <TaskTypeLabel taskType={t} />
            </Badge>
          ))}
        </div>
      </div>
    );

  return (
    <>
      {/* Mapping type selector */}
      <Nav variant="pills" className="mb-4" activeKey={activeView}>
        <Nav.Item>
          <Nav.Link eventKey="tasks">Tasks</Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Acknowledgment banner */}
      <Alert variant={acknowledged ? "warning" : "secondary"} className="mb-4">
        <div className="d-flex justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <span>{acknowledged ? <strong>Editing mode active.</strong> : "These mappings are read-only."}</span>
            {acknowledged && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setSaveError(null);
                  setShowSaveModal(true);
                }}
                disabled={!hasChanges}
              >
                Save Mappings
              </Button>
            )}
          </div>
          <Form.Check
            type="switch"
            id="mapping-acknowledge-toggle"
            label="I acknowledge that changing these mappings might trigger old mails"
            checked={acknowledged}
            onChange={handleAcknowledgeToggle}
          />
        </div>
      </Alert>

      {/* Compass task type cards grouped by automation */}
      {compassTaskTypeGroups
        .filter((g) => !excludeGroups.includes(g.label))
        .map((group) => (
          <div key={group.label} className="mb-4">
            <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
              {group.label}
            </h6>
            <Row xs={1} md={2} lg={3} className="g-3">
              {group.types.map((compassType) => {
                const mappedIds = currentMulti[compassType] ?? [];
                const isMapped = mappedIds.length > 0;
                const usedIds = acknowledged ? getUsedCrmIds(pendingMappings, compassType) : new Set<string>();
                const availableToAdd = crmTaskTypes.filter((t) => !usedIds.has(t.id) && !mappedIds.includes(t.id));

                return (
                  <Col key={compassType}>
                    <Card
                      style={{
                        borderColor: isMapped ? "#28a745" : "#dee2e6",
                        backgroundColor: isMapped ? "#f0fff4" : "#f8f9fa",
                      }}
                    >
                      <Card.Header
                        style={{
                          backgroundColor: isMapped ? "#d4edda" : "#e9ecef",
                          borderBottomColor: isMapped ? "#28a745" : "#dee2e6",
                          fontWeight: 500,
                          fontSize: "0.875rem",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{compassType}</span>
                          {isMapped ? (
                            <Badge bg="success" style={{ fontSize: "0.7rem" }}>
                              {mappedIds.length}
                            </Badge>
                          ) : (
                            <Badge bg="secondary" style={{ fontSize: "0.7rem" }}>
                              unmapped
                            </Badge>
                          )}
                        </div>
                      </Card.Header>
                      <Card.Body className="p-2">
                        {/* Mapped CRM types as removable badges */}
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {mappedIds.map((crmId) => {
                            const crmType = crmTypeById.get(crmId);
                            const name = crmType ? getTaskTypeName(crmType) : crmId;
                            return (
                              <Badge
                                key={crmId}
                                bg="success"
                                className="d-flex align-items-center gap-1"
                                style={{ fontSize: "0.75rem", fontWeight: 400 }}
                              >
                                {crmType ? <TaskTypeLabel taskType={crmType} /> : name}
                                {acknowledged && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCrmType(compassType, crmId)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      cursor: "pointer",
                                      color: "inherit",
                                      lineHeight: 1,
                                      marginLeft: "2px",
                                    }}
                                    aria-label={`Remove ${name}`}
                                  >
                                    ×
                                  </button>
                                )}
                              </Badge>
                            );
                          })}
                          {mappedIds.length === 0 && (
                            <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                              No CRM types mapped
                            </span>
                          )}
                        </div>

                        {/* Add dropdown — only when editing */}
                        {acknowledged && (
                          <Form.Select
                            size="sm"
                            value=""
                            onChange={(e) => {
                              handleAddCrmType(compassType, e.target.value);
                              e.target.value = "";
                            }}
                            disabled={availableToAdd.length === 0}
                          >
                            <option value="">
                              {availableToAdd.length === 0 ? "No more types available" : "+ Add CRM type..."}
                            </option>
                            {[
                              { label: "Calendar", source: "calendar" },
                              { label: "Activity", source: "activity" },
                              { label: "Other", source: "other" },
                            ].map(({ label, source }) => {
                              const optionsForSource = availableToAdd.filter((t) =>
                                source === "other" ? t.source !== "calendar" && t.source !== "activity" : t.source === source,
                              );
                              if (optionsForSource.length === 0) return null;
                              return (
                                <optgroup key={source} label={label}>
                                  {optionsForSource.map((t) => (
                                    <option key={t.id} value={t.id}>
                                      {getTaskTypeName(t)}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })}
                          </Form.Select>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))}

      {/* Remaining CRM types */}
      <div className="mb-4">
        <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
          Remaining CRM types
          {remainingCrmTypes.length > 0 && (
            <Badge bg="secondary" className="ms-2" style={{ fontSize: "0.65rem", verticalAlign: "middle" }}>
              {remainingCrmTypes.length}
            </Badge>
          )}
        </h6>
        {remainingCrmTypes.length === 0 ? (
          <span className="text-muted" style={{ fontSize: "0.8rem" }}>
            All CRM types are mapped or excluded
          </span>
        ) : (
          <>
            {renderRemainingGroup("Calendar", remainingCalendar)}
            {renderRemainingGroup("Activity", remainingActivity)}
            {renderRemainingGroup("Other", remainingOther)}
          </>
        )}
      </div>

      {/* CRM types not to be mapped */}
      <div className="mb-4">
        <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
          CRM types not to be mapped
          {excludedCrmTypes.length > 0 && (
            <Badge bg="dark" className="ms-2" style={{ fontSize: "0.65rem", verticalAlign: "middle" }}>
              {excludedCrmTypes.length}
            </Badge>
          )}
        </h6>
        <div className="d-flex flex-wrap gap-2">
          {excludedCrmTypes.length === 0 ? (
            <span className="text-muted" style={{ fontSize: "0.8rem" }}>
              None
            </span>
          ) : (
            excludedCrmTypes.map((t) => (
              <Badge
                key={t.id}
                bg="dark"
                style={{ fontSize: "0.75rem", fontWeight: 400, cursor: acknowledged ? "pointer" : "default" }}
                onClick={acknowledged ? () => handleUnexcludeCrmType(t.id) : undefined}
                title={acknowledged ? "Click to move back to remaining" : undefined}
              >
                <TaskTypeLabel taskType={t} />
              </Badge>
            ))
          )}
        </div>
      </div>

      {/* Confirmation modal */}
      <Modal show={showSaveModal} onHide={() => !isSaving && setShowSaveModal(false)}>
        <Modal.Header closeButton={!isSaving}>
          <Modal.Title>Confirm Mapping Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger">{saveError}</Alert>}

          {!hasChanges ? (
            <p>No changes to save.</p>
          ) : (
            <>
              {pendingRemoved.length > 0 && (
                <div className="mb-3">
                  <strong className="text-danger">Removed mappings:</strong>
                  <ul className="mt-1 mb-0">
                    {pendingRemoved.map((c) => (
                      <li key={`removed-${c.compassType}-${c.crmId}`}>
                        <em>{c.crmName}</em> <span className="text-muted">removed from</span>{" "}
                        <code>{c.compassType}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {pendingAdded.length > 0 && (
                <div className="mb-3">
                  <strong className="text-success">Added mappings:</strong>
                  <ul className="mt-1 mb-0">
                    {pendingAdded.map((c) => (
                      <li key={`added-${c.compassType}-${c.crmId}`}>
                        <em>{c.crmName}</em> <span className="text-muted">added to</span> <code>{c.compassType}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {pendingExcludedAdded.length > 0 && (
                <div className="mb-3">
                  <strong className="text-secondary">Marked as not to be mapped:</strong>
                  <ul className="mt-1 mb-0">
                    {pendingExcludedAdded.map((c) => (
                      <li key={`excl-added-${c.crmId}`}>
                        <em>{c.crmName}</em>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {pendingExcludedRemoved.length > 0 && (
                <div className="mb-3">
                  <strong className="text-secondary">Unmarked as not to be mapped:</strong>
                  <ul className="mt-1 mb-0">
                    {pendingExcludedRemoved.map((c) => (
                      <li key={`excl-removed-${c.crmId}`}>
                        <em>{c.crmName}</em>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmSave} disabled={isSaving || !hasChanges}>
            {isSaving ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Confirm & Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default IntegrationMappings;
